import { useColorTags } from '@entities/common';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { Note } from '@widgets/clients';
import { ClientMenu } from '@features/clients/client-menu';
import { Profile } from '@features/clients/client-profile/ui';
import { ColorTagsList } from '@features/clients/colors-list';
import { ConsultationList } from '@features/clients/consultations-list';
import { AcquiredEventsList } from '@features/clients/events-acquired-list';
import { GroupsList } from '@features/clients/groups-list';
import { ContactsList, RentedArticleList, RentedProgramList } from '@features/clients/index.ts';
import { NoteList } from '@features/clients/note-list';
import { Search, useFilter } from '@features/common';
import {
  Add,
  ArrowShortLeft,
  MailClientProfile,
  MessageClientProfile,
  PhoneClientProfile,
  Search as SearchIcon,
} from '@shared/assets';
import { IconButton, ListBody, PageHead, PageTitle, TextButton } from '@shared/ui';
import { useClient } from '@src/entities/clients';
import type { Note as Notemodel } from 'library/models/clients';
import { useTabs } from '../../model';
import * as st from './styles.module.scss';

export const ClientActiveTabMobile: FC = () => {
  const { selectedModule, toggleActionModal } = useTabs();

  const { clientId, tabCode } = useParams();

  const { client } = useClient(Number(clientId));

  const { colorTags, selectedColorTag, setSelectedColorTag, resetSelectedColorTag } =
    useColorTags();

  const navigate = useNavigate();

  const [isSearchMobile, setIsSearchMobile] = useState(false);

  const { searchNotes, onSearchNotes } = useFilter();

  const moduleName = () => {
    if (selectedModule?.verbose_name) return selectedModule.verbose_name;
    if (tabCode === 'notes') return 'Заметки';
    if (tabCode === 'contacts') return 'Контакты';
    if (tabCode === 'info') return 'Профиль';
    if (tabCode === 'groups') return 'Группы';
    if (tabCode === 'colortag') return 'Теги';
    if (tabCode === 'events') return 'Мероприятии';
    if (tabCode === 'schedules') return 'Консультации';
    if (tabCode === 'library' || tabCode === 'without-library') return 'Видео';
    if (tabCode === 'articles') return 'Статьи';

    return 'Клиент';
  };

  const tabCodeAdd = ['notes', 'programs', 'schedules', 'groups'];

  useEffect(() => {
    moduleName();
  }, [tabCode, clientId]);

  const onRedirectBack = () => navigate(`/clients/clients/${clientId}`);

  const PageHeadRightNode = useMemo(() => {
    if (tabCode === 'groups') {
      return (
        <>
          <Tooltip title="Добавить">
            <div>
              <IconButton variant="dark" onClick={() => toggleActionModal()}>
                <Add />
              </IconButton>
            </div>
          </Tooltip>
          <TextButton onClick={onRedirectBack}>Готово</TextButton>
        </>
      );
    }
    if (tabCode === 'contacts') {
      return (
        <>
          <Tooltip title="Добавить">
            <div>
              <IconButton variant="dark" onClick={() => toggleActionModal()}>
                <Add />
              </IconButton>
            </div>
          </Tooltip>
        </>
      );
    }
    if (tabCode === 'colortag') {
      return (
        <>
          <TextButton onClick={onRedirectBack}>Готово</TextButton>
        </>
      );
    }
    if (tabCode === 'about') {
      return (
        <>
          <IconButton
            // isActive
            // needHover
            onClick={() => window.open(`tel:${client?.phonenumber ? client.phonenumber : ''}`)}
          >
            <PhoneClientProfile />
          </IconButton>
          <IconButton
            // isActive
            // needHover
            onClick={() => window.open(`tel:${client?.phonenumber ? client.phonenumber : ''}`)}
          >
            <MessageClientProfile />
          </IconButton>
          <IconButton
            // isActive
            // needHover
            onClick={() => window.open(`mailto:${client?.email ? client.email : ''}`)}
          >
            <MailClientProfile />
          </IconButton>
          <ClientMenu client={client} />
        </>
      );
    } else if (tabCode && tabCodeAdd.includes(tabCode)) {
      return (
        <>
          {!isSearchMobile && (
            <>
              <Tooltip title="Поиск">
                <div>
                  <IconButton variant="mobile" onClick={() => setIsSearchMobile(!isSearchMobile)}>
                    <SearchIcon />
                  </IconButton>
                </div>
              </Tooltip>

              <Tooltip title="Добавить">
                <div>
                  <IconButton variant="dark" onClick={() => toggleActionModal()}>
                    <Add />
                  </IconButton>
                </div>
              </Tooltip>
            </>
          )}
        </>
      );
    }
  }, [client, isSearchMobile, tabCode, tabCodeAdd, toggleActionModal]);
  return (
    <>
      {isSearchMobile && (
        <>
          <PageTitle title="Поиск" />
          <PageHead
            Left={
              <IconButton
                variant="dark"
                onClick={() => {
                  setIsSearchMobile(!isSearchMobile);
                  onSearchNotes('');
                }}
              >
                <ArrowShortLeft />
              </IconButton>
            }
            Right={<Search mobile whereUse="notes" search={searchNotes} onSearch={onSearchNotes} />}
          />
        </>
      )}
      {!isSearchMobile && (
        <>
          <PageTitle title={moduleName()} />
          <PageHead
            Left={
              tabCode === 'groups' || tabCode === 'colortag' ? undefined : (
                <button onClick={onRedirectBack}>
                  <IconButton variant="dark">
                    <ArrowShortLeft />
                  </IconButton>
                </button>
              )
            }
            Right={PageHeadRightNode}
          />
        </>
      )}
      <ListBody>
        <section className={st.activeTab__component}>
          {tabCode === 'info' && <Profile id={Number(clientId)} />}
          {tabCode === 'contacts' && <ContactsList id={Number(clientId)} />}
          {tabCode === 'notes' && (
            <NoteList key={clientId} id={Number(clientId)} searchNotes={searchNotes}>
              {(note: Notemodel) => <Note key={note.id} note={note} />}
            </NoteList>
          )}
          {tabCode === 'library' && <RentedProgramList id={Number(clientId)} code={tabCode} />}
          {tabCode === 'articles' && <RentedArticleList clientId={Number(clientId)} />}
          {tabCode === 'schedules' && <ConsultationList id={Number(clientId)} />}
          {tabCode === 'events' && <AcquiredEventsList id={Number(clientId)} code={tabCode} />}
          {tabCode === 'groups' && <GroupsList id={Number(clientId)} />}
          {tabCode === 'colortag' && (
            <ColorTagsList
              id={Number(clientId)}
              colorTags={colorTags}
              selectedColorTag={selectedColorTag!}
              setSelectedColorTag={setSelectedColorTag}
              resetSelectedColorTag={resetSelectedColorTag}
            />
          )}
        </section>
      </ListBody>
    </>
  );
};
